"use client";

import { MessageCircle, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SweetCoin } from "@/components/coins/SweetCoin";
import { Avatar } from "@/components/ui/Avatar";
import Link from "@/components/ui/Link";
import { TimeAgo } from "@/components/ui/TimeAgo";
import { useUi } from "@/components/ui/use-ui";
import { announceAchievements, type Unlocked } from "@/lib/activity";
import { compact } from "@/lib/format";
import { fmt } from "@/lib/locales/fmt";
import { refreshCoins } from "@/lib/coins-badge";

// A video's comments (lib/comments.ts via /api/videos/<id>/comments): members write, everyone reads.
// Loaded once the section comes near the screen, so the video page itself stays quick and cached.

interface Comment {
  id: string;
  body: string;
  createdAt: string;
  user: { username: string; displayName: string; avatar: string | null };
  mine: boolean;
}
interface Page {
  items: Comment[];
  more: boolean;
  total: number;
  signedIn: boolean;
  coins: number;
  coinsPerDay: number;
}

const MAX = 500;

export function Comments({ videoId }: { videoId: string }) {
  const t = useUi();
  const c = t.comments;
  const box = useRef<HTMLElement>(null);
  const [page, setPage] = useState<Page | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [earned, setEarned] = useState<number | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let live = true;
    const load = () =>
      fetch(`/api/videos/${videoId}/comments`)
        .then((r) => (r.ok ? r.json() : null))
        .then((p: Page | null) => live && p && setPage(p))
        .catch(() => {});
    const seen = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        seen.disconnect();
        void load();
      },
      { rootMargin: "600px 0px" },
    );
    seen.observe(el);
    return () => {
      live = false;
      seen.disconnect();
    };
  }, [videoId]);

  useEffect(() => {
    if (earned === null) return;
    const timer = window.setTimeout(() => setEarned(null), 4000);
    return () => window.clearTimeout(timer);
  }, [earned]);

  const errorText = (code: string | undefined) =>
    code === "short" ? c.short : code === "long" ? fmt(c.long, { n: MAX }) : code === "links" ? c.links : code === "forbidden" ? c.forbidden : code === "rate" ? c.rate : code === "duplicate" ? c.duplicate : c.failed;

  const post = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/videos/${videoId}/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body: text }) });
      const out = (await res.json().catch(() => null)) as { ok?: boolean; error?: string; comment?: Comment; coins?: number; achievements?: Unlocked[] } | null;
      if (out?.ok && out.comment) {
        const added = out.comment;
        setPage((p) => (p ? { ...p, items: [added, ...p.items], total: p.total + 1 } : p));
        setText("");
        if (out.coins) {
          setEarned(out.coins);
          refreshCoins();
        }
        announceAchievements(out.achievements);
      } else setError(errorText(out?.error));
    } catch {
      setError(c.failed);
    } finally {
      setBusy(false);
    }
  };

  const more = async () => {
    const last = page?.items.at(-1);
    if (!page || !last) return;
    const res = await fetch(`/api/videos/${videoId}/comments?before=${encodeURIComponent(last.createdAt)}`).catch(() => null);
    const next = res?.ok ? ((await res.json()) as Page) : null;
    if (next) setPage({ ...page, items: [...page.items, ...next.items], more: next.more });
  };

  const remove = async (id: string) => {
    const res = await fetch(`/api/videos/${videoId}/comments?comment=${id}`, { method: "DELETE" }).catch(() => null);
    if (res?.ok) setPage((p) => (p ? { ...p, items: p.items.filter((i) => i.id !== id), total: Math.max(0, p.total - 1) } : p));
  };

  return (
    <section ref={box} aria-labelledby="comments-title" className="mt-8 max-w-3xl">
      <h2 id="comments-title" className="flex items-center gap-2 text-[17px] text-fg">
        <MessageCircle size={19} className="text-soft" />
        {c.title}
        {page && page.total > 0 && <span className="text-[14px] text-muted">{compact(page.total)}</span>}
      </h2>

      {!page ? (
        <div aria-hidden className="skeleton mt-4 h-24 rounded-2xl" />
      ) : (
        <>
          {page.signedIn ? (
            <form onSubmit={post} className="mt-4">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={MAX}
                rows={2}
                placeholder={c.placeholder}
                aria-label={c.placeholder}
                className="block w-full resize-y rounded-2xl border border-line bg-white/[0.04] px-4 py-3 text-[14.5px] text-fg placeholder:text-faint focus:border-white/30 focus:outline-none"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="flex flex-1 items-center gap-1.5 text-[12.5px] text-faint">
                  <SweetCoin size={14} />
                  {earned ? <span className="text-[#f6c453]">{fmt(c.earned, { n: earned })}</span> : fmt(c.coinsHint, { n: page.coins, max: page.coinsPerDay })}
                </p>
                <button
                  type="submit"
                  disabled={busy || !text.trim()}
                  className="h-9 rounded-full bg-white px-5 text-[13.5px] font-medium text-black transition-opacity hover:bg-white/85 disabled:opacity-50"
                >
                  {c.post}
                </button>
              </div>
              {error && (
                <p role="alert" className="mt-2 text-[13px] text-brand-soft">
                  {error}
                </p>
              )}
            </form>
          ) : (
            <Link
              href={`/login?next=${encodeURIComponent(`/-${videoId}`)}`}
              className="chip mt-4 flex h-11 items-center justify-center rounded-2xl text-[14px] text-soft hover:text-fg"
            >
              {c.login}
            </Link>
          )}

          {page.items.length ? (
            <ul className="mt-5 space-y-4">
              {page.items.map((item) => (
                <li key={item.id} className="flex gap-3">
                  <Avatar name={item.user.displayName} seed={item.user.username} src={item.user.avatar} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-[13px]">
                      <span className="truncate text-fg">{item.user.displayName}</span>
                      <span className="text-faint">
                        <TimeAgo iso={item.createdAt} />
                      </span>
                      {item.mine && (
                        <button
                          type="button"
                          onClick={() => remove(item.id)}
                          aria-label={c.delete}
                          title={c.delete}
                          className="ml-auto grid size-7 place-items-center rounded-full text-faint hover:bg-white/10 hover:text-fg"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </p>
                    <p className="mt-0.5 whitespace-pre-line break-words text-[14px] leading-relaxed text-soft">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-5 text-[13.5px] text-muted">{c.empty}</p>
          )}
          {page.more && (
            <button type="button" onClick={more} className="chip mt-4 h-9 rounded-full px-4 text-[13.5px] text-soft hover:text-fg">
              {c.more}
            </button>
          )}
        </>
      )}
    </section>
  );
}
